import { Directive, inject, Input, type OnDestroy, type OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import {
    IAttributeConfData,
    IAttributeConfDataOption
} from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import type { IProductDataV3 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { IAddToBasketData } from '@df/session/api/basket/basket';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ProductDecorator } from '../../../../model/product/product.decorator';
import { NBackInStockModal } from '../../back-in-stock/back-in-stock-modal.interface';
import { ProductConfigurableOptionsAbstract } from '../../configurable-options/product-configurable-options.abstract';
import { ProductConfigurableOptionsService } from '../../configurable-options/product-configurable-options.service';
import { PRODUCT_CLOSE_PREVIEW_EVENT_NAME, PRODUCT_OPEN_PREVIEW_EVENT_NAME, ProductEventData } from '../../product.abstract';
import { ProductService } from '../../product.service';
import { NProductSizeGuideModal } from '../../size-guide/modal/product-size-guide-modal.interface';

@Directive()
export abstract class ProductListingConfigurableOptionsAbstract extends ProductConfigurableOptionsAbstract implements OnInit, OnDestroy {
    @Input()
    color: number | undefined;

    @Input()
    hidePrefix = false;

    @Input()
    listPosition?: number;

    readonly pattern = /(UK |US )/g;

    @ViewChild('trayModalContentTemplateRef')
    protected set trayModalContentTemplateRef(value: TemplateRef<unknown>) {
        this._trayModalContentTemplateRef = value;

        this.readyToOpen();
    }
    get trayModalContentTemplateRef(): TemplateRef<unknown> {
        return this._trayModalContentTemplateRef;
    }
    protected _trayModalContentTemplateRef!: TemplateRef<unknown>;

    /**
     * Option that is currently being added to basket
     */
    processedOption?: IAttributeConfDataOption;

    status = new Status();
    protected basketAddNotifyTimeout?: number;

    readonly client = inject(Client);
    protected productConfigurableOptionsService = inject(ProductConfigurableOptionsService);
    protected productService = inject(ProductService);
    protected dfModalService = inject(DfModalService);
    protected attributeLabelPipeService = inject(AttributeLabelPipeService);
    protected readonly disposableTimeoutService = injectTimeoutService();

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));

        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.product, this.listPosition)) {
                    if (event.id === PRODUCT_OPEN_PREVIEW_EVENT_NAME) {
                        this.onOpenEvent(event.data);
                    }

                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        this.onCloseEvent(event.data);
                    }
                }
            })
        );
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['color']) {
            this.onDataChange(false);
        }
    }

    override ngOnDestroy(): void {
        this.closeTray();

        super.ngOnDestroy();
    }

    get hasLetterOption() {
        return !!(<ProductDecorator>this.parent).hasLetterOption;
    }

    get hasSizeOption() {
        return !!(<ProductDecorator>this.parent).hasSizeOption;
    }

    /**
     * Options to present (we only support one attribute in this component!)
     */
    get sizeOptions(): IAttributeConfDataOption[] {
        if (!this.sizeAttribute) {
            return [];
        }

        return this.sizeAttribute.options.map(o => ({
            ...o,
            padding: `${o.label}`?.length > 2
        }));
    }

    get sizeAttribute() {
        return this.attributes?.find(i => i.code === 'size' || i.code === 'shoe_size' || i.code === 'accessory_size');
    }

    get needSelectAttribute(): string | undefined {
        const data = this.data || {};

        return this.product?.parent?.configurableAttributeCodes?.find(i => !data[i]);
    }

    readyToOpen() {}

    override onDataChange(force = false) {
        if (this.attributes?.find(i => i.code === 'color') && this.color) {
            this.data['color'] = this.color;

            this.status.reset();

            if (this.basketAddNotifyTimeout) {
                clearTimeout(this.basketAddNotifyTimeout);
                this.basketAddNotifyTimeout = undefined;
            }
        }

        super.onDataChange(force);
    }

    override pick(attribute: string | IAttributeConfData, option: IAttributeConfDataOption) {
        super.pick(attribute, option);

        this.processedOption = option;

        this.detectChanges();
    }

    override isPicked(attribute: string | IAttributeConfData, option: IAttributeConfDataOption) {
        return this.processedOption?.value === option.value;
    }

    /**
     * Buy (when clicking an option)
     */
    buy(option: IAttributeConfDataOption) {
        // do nothing while busy
        if (this.status.busy) {
            return;
        }
        // if option is OOS do nothing
        if (!option.salable) {
            return;
        }

        // send add to basket request
        const cleanup = (result?: any) => {
            this.processedOption = undefined;

            if (this.basketAddNotifyTimeout) {
                clearTimeout(this.basketAddNotifyTimeout);
                this.basketAddNotifyTimeout = undefined;
            }

            this.basketAddNotifyTimeout = this.disposableTimeoutService.setTimeout(() => {
                this.closeTray();
            }, 5000);
        };

        this.basketService.add(this.status, this.getOptionBuyRequest(option), cleanup, cleanup);
    }

    handleClick(): void {
        const option = this.processedOption;

        if (!option) {
            return;
        }

        const missingAttrCode = this.needSelectAttribute;

        const callback = () => this.buy(option);

        if (missingAttrCode) {
            if (missingAttrCode === 'size' || missingAttrCode === 'shoe_size' || missingAttrCode === 'accessory_size') {
                this.status.error = 'Select your size';
            } else {
                this.status.error = `Please select ${this.attributeLabelPipeService.execute(missingAttrCode)}`;
            }

            return;
        } else {
            if (!option.salable) {
                if (this.client.isX || this.client.isL) {
                    this.app.events.broadcast('xnotif-open');
                } else {
                    this.dfModalService.open(undefined, NBackInStockModal.ID, undefined, this.product);
                }
                return;
            }

            callback();
        }
    }

    openSizeGuide() {
        this.dfModalService.open(undefined, NProductSizeGuideModal.ID, undefined, this.product);
    }

    /**
     * Per option status
     * we only return status during adding to basket
     */
    getStatus(option?: IAttributeConfDataOption): Status | undefined {
        return option === this.processedOption ? this.status : undefined;
    }

    protected getOptionSwatch(option: IAttributeConfDataOption) {
        if (option && typeof option !== 'string') {
            return option.swatch || '#fff';
        }

        return '#fff';
    }

    protected closeTray(): void {
        this.productConfigurableOptionsService.closeTray();
    }

    protected onCloseEvent(data: ProductEventData) {
        if (this.client.isS || this.client.isM) {
            this.closeTray();
        }
    }

    protected onOpenEvent(data: ProductEventData) {
        this.onDataChange(false);

        if (this.client.isS || this.client.isM) {
            this.openTray(this.trayModalContentTemplateRef);
        }
    }

    protected openTray(template: TemplateRef<unknown>) {
        this.productConfigurableOptionsService.openTray(
            template,
            () => this.onTrayOpen(),
            () => this.onTrayClose()
        );
    }

    protected onTrayClose(): void {
        this.dfEventService.dispatch({
            id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
            data: {
                product: this.product as ProductDecorator<IProductDataV3>,
                index: this.listPosition,
                callback: component => {
                    component.basketAdded = false;
                }
            },
            action: () => {
                if (this.basketAddNotifyTimeout) {
                    clearTimeout(this.basketAddNotifyTimeout);
                    this.basketAddNotifyTimeout = undefined;
                }

                this.status.reset();
            }
        });
    }

    protected onTrayOpen(): void {}

    /**
     * Prepares buy request for a product
     */
    protected getOptionBuyRequest(option: IAttributeConfDataOption): IAddToBasketData {
        return {
            id: option.product.id,
            qty: 1
        };
    }
}
